import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import WorkerSignupLayout, {
  NAVY, AMBER, WHITE, FONT_HEAD, FONT_BODY, StepPills,
} from './WorkerSignupLayout';

const inp = {
  width: '100%', padding: '11px 13px', fontSize: '0.95rem',
  border: '1.5px solid #E2E6EB', borderRadius: '8px',
  color: NAVY, outline: 'none', backgroundColor: WHITE,
};
const sel = { ...inp, cursor: 'pointer' };
const lbl = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: NAVY, marginBottom: '5px' };
const fld = { marginBottom: '1.25rem' };
const g2  = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' };
const card = {
  backgroundColor: WHITE, borderRadius: '16px',
  boxShadow: '0 2px 24px rgba(0,0,0,0.07)',
  padding: '2rem', marginBottom: '1.25rem',
};

// `value` is stored verbatim in worker_trades.trade and matched (case-insensitive)
// against apprenticeship/job `.trade` fields elsewhere in the app -- it must stay
// stable English regardless of UI language. Only the rendered label is translated,
// via the shared worker.tradeNames.* keys (also used by WorkerDashboard's own
// trade dropdown, which has a slightly different but overlapping trade list).
const TRADES = [
  { value: 'Electrician',                       key: 'electrician' },
  { value: 'Plumber',                            key: 'plumber' },
  { value: 'Welder and Fabricator',              key: 'welderFabricator' },
  { value: 'HVAC Technician',                    key: 'hvacTechnician' },
  { value: 'Carpenter',                          key: 'carpenter' },
  { value: 'Elevator Installer',                 key: 'elevatorInstaller' },
  { value: 'Solar Panel Installer',              key: 'solarPanelInstaller' },
  { value: 'Industrial Maintenance Mechanic',    key: 'industrialMaintenanceMechanic' },
  { value: 'CNC Operator',                       key: 'cncOperator' },
  { value: 'Diesel Mechanic',                    key: 'dieselMechanic' },
  { value: 'Structural Iron and Steel Worker',   key: 'structuralIronSteelWorker' },
  { value: 'Sheet Metal Worker',                 key: 'sheetMetalWorker' },
  { value: 'Pipefitter and Steamfitter',         key: 'pipefitterSteamfitter' },
  { value: 'CDL/Truck Driver',                   key: 'cdlTruckDriver' },
  { value: 'Other Skilled Trade',                key: 'otherSkilledTrade' },
];

// Same stable-value/translated-label pattern -- experience_level and years_experience
// are stored and displayed back (WorkerProfile.jsx, WorkerStep5.jsx review) but never
// used for filtering/matching, unlike `trade` above.
const EXPERIENCE_OPTIONS = [
  { value: 'Apprentice',  key: 'apprentice' },
  { value: 'Journeyman',  key: 'journeyman' },
  { value: 'Foreman',     key: 'foreman' },
  { value: 'Master',      key: 'master' },
  { value: 'Other',       key: 'other' },
];

const YEARS_OPTIONS = [
  { value: 'Less than 1 year', key: 'lessThan1' },
  { value: '1 to 3 years',     key: 'oneToThree' },
  { value: '4 to 6 years',     key: 'fourToSix' },
  { value: '7 to 10 years',    key: 'sevenToTen' },
  { value: '10 plus years',    key: 'tenPlus' },
];

function ToggleGroup({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
      {options.map(opt => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            style={{
              padding: '9px 20px', borderRadius: '8px',
              border: `2px solid ${active ? NAVY : '#E2E6EB'}`,
              backgroundColor: active ? NAVY : WHITE,
              color: active ? AMBER : 'rgba(13,27,42,0.55)',
              fontWeight: 600, fontSize: '0.875rem',
              cursor: 'pointer', fontFamily: FONT_BODY,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export default function WorkerStep3() {
  const navigate    = useNavigate();
  const { t } = useTranslation();
  const tradeRef    = useRef(null);
  const [trades, setTrades]         = useState([]);
  const [availability, setAvail]    = useState('Full-time');
  const [unionPref, setUnion]       = useState('both');
  const [saveError, setSaveError]   = useState('');
  const [tradeError, setTradeError] = useState('');
  const [form, setForm] = useState({
    experience: '', years: '', minRate: '', targetRate: '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Reload previously-saved answers so navigating back and then forward again
  // doesn't show blank fields even though this step's data already exists.
  useEffect(() => {
    const loadExisting = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) return;
      const { data: row } = await supabase.from('worker_trades')
        .select('trade, experience_level, years_experience, min_hourly_rate, target_hourly_rate, availability, union_interest')
        .eq('worker_id', userId).maybeSingle();
      if (row) {
        setTrades(row.trade ? row.trade.split(', ').filter(Boolean) : []);
        setAvail(row.availability || 'Full-time');
        setUnion(row.union_interest || 'both');
        setForm(p => ({
          ...p,
          experience: row.experience_level || '',
          years: row.years_experience || '',
          minRate: row.min_hourly_rate || '',
          targetRate: row.target_hourly_rate || '',
        }));
      }
    };
    loadExisting();
  }, []);

  const handleContinue = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    const userId = session?.user?.id
    if (!userId) { navigate('/worker/signup'); return }

    // A trade is what jobs are matched on, so this step can't be saved without one. Checked
    // here, before any request, so a row with an empty trade is never written.
    const tradeValue = trades.map(x => (x || '').trim()).filter(Boolean).join(', ')
    if (!tradeValue) {
      setTradeError(t('worker.signup.step3.tradeRequired'))
      tradeRef.current?.focus()
      return
    }
    setTradeError('');
    setSaveError('');
    const row = {
      trade: tradeValue,
      experience_level: form.experience,
      years_experience: form.years,
      min_hourly_rate: form.minRate,
      target_hourly_rate: form.targetRate,
      availability,
      union_interest: unionPref
    }
    // Update the existing row in place, or insert the first one. This used to delete and then
    // insert with neither result checked, so a failed insert left the worker with no trade at
    // all. worker_trades has no unique constraint on worker_id, so upsert isn't available.
    // Only advance once the saved row comes back.
    const { data: existing, error: readError } = await supabase.from('worker_trades')
      .select('id').eq('worker_id', userId).order('id').limit(1)
    if (readError) { setSaveError(t('worker.signup.saveFailed')); return }
    const { data: saved, error } = existing?.length
      ? await supabase.from('worker_trades').update(row).eq('id', existing[0].id).select('id')
      : await supabase.from('worker_trades').insert({ worker_id: userId, ...row }).select('id')
    if (error || saved?.length !== 1) { setSaveError(t('worker.signup.saveFailed')); return }
    navigate('/worker/signup/resume')
  }

  const handleTradeSelect = e => {
    const val = e.target.value;
    if (val && !trades.includes(val)) setTrades(prev => [...prev, val]);
    if (val) setTradeError('');
    e.target.value = '';
  };
  const removeTrade = t => setTrades(prev => prev.filter(x => x !== t));

  return (
    <WorkerSignupLayout
      step={3}
      tipText={t('worker.signup.step3.tip')}
      onBack={() => navigate('/worker/signup/personal')}
      onContinue={handleContinue}
    >
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: AMBER, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '1rem' }}>
        {t('worker.signup.step3.eyebrow')}
      </div>

      <StepPills current={3} />

      <div style={card}>
        <h1 style={{ fontFamily: FONT_HEAD, fontSize: '2.1rem', color: NAVY, letterSpacing: '0.03em', marginBottom: '0.35rem' }}>
          {t('worker.signup.step3.title')}
        </h1>
        <p style={{ fontSize: '0.9rem', color: 'rgba(13,27,42,0.52)', marginBottom: '1.75rem', lineHeight: 1.5 }}>
          {t('worker.signup.step3.subtitle')}
        </p>

        {/* Trade select */}
        <div style={fld}>
          <label htmlFor="signup-trade" style={lbl}>{t('worker.signup.step3.tradeLabel')}</label>
          <select
            ref={tradeRef}
            id="signup-trade"
            aria-required="true"
            aria-invalid={tradeError ? 'true' : 'false'}
            aria-describedby={tradeError ? 'signup-trade-error' : undefined}
            style={tradeError ? { ...sel, borderColor: '#B91C1C' } : sel}
            defaultValue=""
            onChange={handleTradeSelect}
          >
            <option value="" disabled>{t('worker.signup.step3.tradePlaceholder')}</option>
            {TRADES.filter(tr => !trades.includes(tr.value)).map(tr => (
              <option key={tr.value} value={tr.value}>{t(`worker.tradeNames.${tr.key}`)}</option>
            ))}
          </select>

          {/* Chips */}
          {trades.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.75rem' }}>
              {trades.map(tradeValue => {
                const tr = TRADES.find(x => x.value === tradeValue);
                return (
                  <div key={tradeValue} style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    backgroundColor: AMBER, color: NAVY,
                    borderRadius: '999px', padding: '5px 12px',
                    fontSize: '0.82rem', fontWeight: 600,
                  }}>
                    {tr ? t(`worker.tradeNames.${tr.key}`) : tradeValue}
                    <button
                      type="button"
                      onClick={() => removeTrade(tradeValue)}
                      style={{
                        background: 'none', border: 'none', padding: '0 0 0 2px',
                        cursor: 'pointer', color: NAVY, fontWeight: 700,
                        fontSize: '0.9rem', lineHeight: 1, fontFamily: FONT_BODY,
                      }}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {tradeError && (
            <p id="signup-trade-error" role="alert" data-testid="trade-required-error" style={{ margin: '0.5rem 0 0', fontSize: '0.85rem', fontWeight: 600, color: '#B91C1C', lineHeight: 1.45 }}>
              {tradeError}
            </p>
          )}
          <a
            href="#"
            onClick={e => { e.preventDefault(); tradeRef.current?.focus(); }}
            style={{ display: 'inline-block', marginTop: '0.6rem', color: AMBER, fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none' }}
          >
            {t('worker.signup.step3.addAnotherTrade')}
          </a>
        </div>

        {/* Experience level */}
        <div style={fld}>
          <label style={lbl}>{t('worker.signup.step3.experienceLabel')}</label>
          <select style={sel} value={form.experience} onChange={e => set('experience', e.target.value)}>
            <option value="">{t('worker.signup.step3.experiencePlaceholder')}</option>
            {EXPERIENCE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{t(`worker.signup.step3.experienceOptions.${opt.key}`)}</option>
            ))}
          </select>
        </div>

        {/* Years of experience */}
        <div style={fld}>
          <label style={lbl}>{t('worker.signup.step3.yearsLabel')}</label>
          <select style={sel} value={form.years} onChange={e => set('years', e.target.value)}>
            <option value="">{t('worker.signup.step3.yearsPlaceholder')}</option>
            {YEARS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{t(`worker.signup.step3.yearsOptions.${opt.key}`)}</option>
            ))}
          </select>
        </div>

        {/* Rates */}
        <div style={g2}>
          <div>
            <label style={lbl}>{t('worker.signup.step3.minRateLabel')}</label>
            <input style={inp} placeholder={t('worker.signup.step3.minRatePlaceholder')} value={form.minRate} onChange={e => set('minRate', e.target.value)} />
          </div>
          <div>
            <label style={lbl}>{t('worker.signup.step3.targetRateLabel')}</label>
            <input style={inp} placeholder={t('worker.signup.step3.targetRatePlaceholder')} value={form.targetRate} onChange={e => set('targetRate', e.target.value)} />
          </div>
        </div>

        {/* Availability */}
        <div style={fld}>
          <label style={{ ...lbl, marginBottom: '0.65rem' }}>{t('worker.signup.step3.availabilityLabel')}</label>
          <ToggleGroup
            value={availability}
            onChange={setAvail}
            options={[
              { value: 'Full-time',          label: t('worker.signup.step3.availabilityOptions.fullTime') },
              { value: 'Part-time',          label: t('worker.signup.step3.availabilityOptions.partTime') },
              { value: 'Contract/Freelance', label: t('worker.signup.step3.availabilityOptions.contract') },
              { value: 'Temporary',          label: t('worker.signup.step3.availabilityOptions.temporary') },
              { value: 'Seasonal',           label: t('worker.signup.step3.availabilityOptions.seasonal') },
              { value: 'Open to all',        label: t('worker.signup.step3.availabilityOptions.openToAll') },
            ]}
          />
        </div>

        {/* Union preference */}
        <div style={fld}>
          <label style={{ ...lbl, marginBottom: '0.65rem' }}>{t('worker.signup.step3.unionInterestLabel')}</label>
          <ToggleGroup
            value={unionPref}
            onChange={setUnion}
            options={[
              { value: 'both',     label: t('worker.signup.step3.unionInterestOptions.both') },
              { value: 'union',    label: t('worker.signup.step3.unionInterestOptions.union') },
              { value: 'employer', label: t('worker.signup.step3.unionInterestOptions.employer') },
            ]}
          />
        </div>
      </div>
      {saveError && (
        <p role="alert" data-testid="signup-save-error" style={{ margin: '0 0 0.25rem', fontSize: '0.9rem', fontWeight: 600, color: '#B91C1C', lineHeight: 1.45, overflowWrap: 'anywhere' }}>
          {saveError}
        </p>
      )}
    </WorkerSignupLayout>
  );
}
